import { Link } from '@mui/material';
import { Description } from '@mui/icons-material';

// ---------------------------------------------------------
// Widget Type Enum and Display Names
// ---------------------------------------------------------
export enum WidgetType {
  BannerV1 = "BannerV1",
  BannerV2 = "BannerV2",
  BlogPost = "BlogPost",
  BlogsListing = "BlogsListing",
  BlogV1 = "BlogV1",
  CompanyDetails = "CompanyDetails",
  Contact = "Contact",
  ProjectDetails = "ProjectDetails",
  ProjectsListing = "ProjectsListing",
  ProjectsV1 = "ProjectsV1",
  ServiceDetails = "ServiceDetails",
  ServicePage = "ServicePage",
  ServicesV1 = "ServicesV1",
  SidebarV1 = "SidebarV1",
  WhoWeAreV1 = "WhoWeAreV1",
  TestimonalV1 = "TestimonalV1",
  MediaTextV1 = "MediaTextV1"
}

export const WidgetTypeDisplayNames: Record<WidgetType, string> = {
  [WidgetType.BannerV1]: "BannerV1",
  [WidgetType.BannerV2]: "BannerV2",
  [WidgetType.BlogPost]: "BlogPost",
  [WidgetType.BlogsListing]: "BlogsListing",
  [WidgetType.BlogV1]: "BlogV1",
  [WidgetType.CompanyDetails]: "CompanyDetails",
  [WidgetType.Contact]: "Contact",
  [WidgetType.ProjectDetails]: "ProjectDetails",
  [WidgetType.ProjectsListing]: "ProjectsListing",
  [WidgetType.ProjectsV1]: "ProjectsV1",
  [WidgetType.ServiceDetails]: "ServiceDetails",
  [WidgetType.ServicePage]: "ServicePage",
  [WidgetType.ServicesV1]: "ServicesV1",
  [WidgetType.SidebarV1]: "SidebarV1",
  [WidgetType.WhoWeAreV1]: "WhoWeAreV1",
  [WidgetType.TestimonalV1]: "TestimonalV1",
  [WidgetType.MediaTextV1]: "MediaTextV1"
};

// ---------------------------------------------------------
// Base Interface for All Widgets
// ---------------------------------------------------------
export interface IWidgetType {
  type: WidgetType;
  order: number;
}

// ---------------------------------------------------------
// BannerV1 Widget
// ---------------------------------------------------------
export interface IBannerV1Widget extends IWidgetType {
  Items: {
    Title: string;
    Subtitle: string;
    ImageUrl: string;
  }[];
}

// ---------------------------------------------------------
// BannerV2 Widget
// ---------------------------------------------------------

export interface IBannerV2Widget extends IWidgetType {
  Title: string;
  Items: [
    {
      LinkTitle: string;
      Link: string;
    }
  ];
}

// ---------------------------------------------------------
// WhoWeAreV1 Widget – Exactly 2 items with different structure
// ---------------------------------------------------------
export interface IWhoWeAreItem1 {
  Title: string;
  Description: string;
}
export interface IWhoWeAreItem2 {
  Description: string;
  LinkTitle: string;
  Link: string;
}
export interface IWhoWeAreV1Widget extends IWidgetType {
  Items: [IWhoWeAreItem1, IWhoWeAreItem2];
}

// ---------------------------------------------------------
// ServicesV1 Widget – List of services with images and links
// ---------------------------------------------------------
export interface IServiceV1Item {
  Title: string;
  ImageUrl: string;
  Description: string;
  LinkTitle: string;
  Link: string;
}
export interface IServicesV1Widget extends IWidgetType {
  Title: string;
  Description: string;
  Items: IServiceV1Item[];
}

// ---------------------------------------------------------
// ProjectsV1 Widget – List of project images with descriptions
// ---------------------------------------------------------
export interface IProjectsV1Widget extends IWidgetType {
  Title: string;
  Description: string;
  Subtitle: string;
  LinkTitle: string;
  Link: string;
  Images: string[]; // Expected to contain exactly 7 images
}

// ---------------------------------------------------------
// TestimonialV1 Widget – Main image and exactly 2 items (image and testimonial)
// ---------------------------------------------------------
export interface ITestimonialV1Image {
  ImageUrl: string;
}
export interface ITestimonialV1Item {
  Description: string;
  Title: string;
  Subtitle: string;
}
export interface ITestimonialV1Widget extends IWidgetType {
  ImageUrl: string;
  Items: [ITestimonialV1Image, ITestimonialV1Item];
}

// ---------------------------------------------------------
// BlogV1 Widget – List of blog posts with headers and subtitles
// ---------------------------------------------------------
export interface IBlogV1Item {
  Title: string;
  Description: string;
  ImageUrl: string;
  Link: string;
  Header: string;
  Subtitle: string;
}
export interface IBlogV1Widget extends IWidgetType {
  Title: string;
  Description: string;
  Items: IBlogV1Item[];
}

// ---------------------------------------------------------
// ProjectsListing Widget – Title, Filters, and list of projects
// ---------------------------------------------------------
export interface IProjectsListingFilter {
  Title: string;
  Properties: {
    Filter: string;
  };
}
export interface IProjectsListingItem {
  Title: string;
  Subtitle: string;
  Category: string;
  ImageUrl: string;
  Link: string;
}
export interface IProjectsListingWidget extends IWidgetType {
  Title: string;
  Filters: IProjectsListingFilter[];
  Items: IProjectsListingItem[];
}

// ---------------------------------------------------------
// ServicePage Widget – Title and list of services
// ---------------------------------------------------------
export interface IServicePageItem {
  Title: string;
  ImageUrl: string;
  Description: string;
  Link: string;
  LinkTitle: string;
}

export interface IServicePageFilter {
  Link: string;
  Title: string;
}

export interface IServicePageWidget extends IWidgetType {
  Title: string;
  Items: IServicePageItem[];
  Filters: IServicePageFilter[]
}

// ---------------------------------------------------------
// ServiceDetails Widget – Detailed service information with accordion and steps
// ---------------------------------------------------------
export interface IServiceDetailsAccordion {
  Question: string;
  Answer: string;
}
export interface IServiceDetailsStep {
  Step: string;
  Title: string;
  Text: string;
}
export interface IServiceDetailsProperties {
  ExtraParagraph: string;
  Accordion: IServiceDetailsAccordion[];
  Steps: IServiceDetailsStep[];
}
export interface IServiceDetailsWidget extends IWidgetType {
  Title: string;
  Header: string;
  Description: string;
  ImageUrl: string;
  Properties: IServiceDetailsProperties;
  Filters: IServicePageFilter[]
}

// ---------------------------------------------------------
// ProjectDetails Widget – Detailed project info including navigation buttons, gallery, etc.
// ---------------------------------------------------------
export interface IProjectGallery {
  Detail: string[];
}

export interface IProjectProgress {
  General: number;
  Phase1: number;
  Phase2: number;
  Phase3: number;
}

export interface IProjectDetail {
  Title: string;
  Description: string;
}

export interface IProjectDetailsProperties {
  City: string;
  Gallery: IProjectGallery;
  Paragraphs: string[];
  ProjectDetails: IProjectDetail[];
  // ProjectLocation: string;
  // Progress: IProjectProgress;
  // ProjectType: string;
  // ProjectCost: string;
  // Client: string;
}

export interface IProjectDetailsWidget extends IWidgetType {
  Title: string;
  Properties: IProjectDetailsProperties;
}

// ---------------------------------------------------------
// Contact Widget – Contact info and embedded map URL
// ---------------------------------------------------------
export interface IContactProperties {
  CallHeading: string;
  Phone: string;
  EmailHeading: string;
  Email: string;
  LocationHeading: string;
  Location: string;
  CorporateHeading: string;
  CorporateDescription: string;
  MapUrl: string;
}
export interface IContactWidget extends IWidgetType {
  Title: string;
  Header: string;
  Description: string;
  Properties: IContactProperties;
}

// ---------------------------------------------------------
// CompanyDetails Widget – Company information including counters, history, leadership, etc.
// ---------------------------------------------------------
export interface ICompanyCounter {
  Value: string;
  Label: string;
}
export interface ICompanyProperties {
  Counters: ICompanyCounter[];
}
export interface ICompanyItem {
  Header?: string;
  Title?: string;
  ImageUrl?: string;
  Description?: string;
  Properties?: ICompanyProperties;
  Items?: {
    Date?: string;
    Title?: string;
    Description?: string;
  }[];
  Tabs?: {
    Title: string;
    Description: string;
  }[];
  Images?: string[];
}

export interface IPageFilter {
  Link: string;
  Title: string;
}

export interface ICompanyDetailsWidget extends IWidgetType {
  Items: ICompanyItem[];
  Filters: IPageFilter[]
}

// ---------------------------------------------------------
// BlogsListing Widget – List of blog posts with extra properties
// ---------------------------------------------------------
export interface IBlogProperties {
  Comments: string;
  Author: string;
}
export interface IBlogListingItem {
  Title: string;
  Subtitle: string;
  Description: string;
  ImageUrl: string;
  Link: string;
  Properties: IBlogProperties;
  Date: string;
}
export interface IBlogsListingWidget extends IWidgetType {
  Title: string;
  Items: IBlogListingItem[];
}

// ---------------------------------------------------------
// BlogPost Widget – A single blog post with details
// ---------------------------------------------------------
export interface IBlogPostWidget extends IWidgetType {
  Title: string;
  Date: string;
  ImageUrl: string;
  Description: string;
}

// ---------------------------------------------------------
// SidebarV1 Widget – Generic sidebar widget (using any[] for now)
// ---------------------------------------------------------
export interface ISidebarV1Widget extends IWidgetType {
  Items: any[];
}

// ---------------------------------------------------------
// MediaTextV1 Widget – Media section with text, images, and link
// ---------------------------------------------------------
export interface IMediaTextV1Widget extends IWidgetType {
  Id: string;
  Title: string;
  Description: string;
  Hint: string;
  Images: string[];
  Link: string;
  LinkTitle: string;
}

// ---------------------------------------------------------
// Combined Widget Types
// ---------------------------------------------------------
export type WidgetTypes =
  | IBannerV1Widget
  | IBannerV2Widget
  | IWhoWeAreV1Widget
  | IServicesV1Widget
  | IServicePageWidget
  | IServiceDetailsWidget
  | IProjectsV1Widget
  | IProjectsListingWidget
  | IProjectDetailsWidget
  | IContactWidget
  | ICompanyDetailsWidget
  | IBlogsListingWidget
  | IBlogPostWidget
  | ISidebarV1Widget
  | ITestimonialV1Widget
  | IBlogV1Widget
  | IMediaTextV1Widget;